"use client"

import React, { useEffect, useRef, useState } from "react"
import { Switch, Route, useParams } from "react-router-dom"
import InputArea from "./input_area/input_area.js"
import TransferDialog from "./transfer_dialog/transfer_dialog.js"
import ArchiveDialog from "./archive_dialog/archive_dialog.js"
import ImageViewer from "./image_viewer/image_viewer.js"
import { Tooltip, Button, Spinner } from "@blueprintjs/core"
import Avatar from "../avatar/avatar.js"
import SpaceInvader from "../chat_space.png"
import classNames from "classnames"
import Agente from "../models/Agente"
import CryptoJS from "crypto-js"

import { MessageAgent, MessageDate, MessageReceived, MessageSystem, MessageSent, MESSAGE_TYPES } from "./message/message";

const secretKey = "1F04iqoixAOvibnsDRt"

const sanitizeEmail = (email) =>
  (email || "")
    .toString()
    .trim()

const normalizeEmailWithSpaces = (email) =>
  sanitizeEmail(email).replace(/\./g, " ").replace(/\s+/g, " ").trim()

const normalizeEmailComparable = (email) =>
  normalizeEmailWithSpaces(email).replace(/\s+/g, "")

const toDotEmail = (email) => normalizeEmailWithSpaces(email).replace(/ /g, ".")

const getEmailVariants = (email) => {
  const withSpaces = normalizeEmailWithSpaces(email)
  const withDots = toDotEmail(email)
  return Array.from(new Set([withSpaces, withDots].filter(Boolean)))
}

const getCurrentAgentEmailVariants = (currentAgent) => {
  const rawEmails = [
    currentAgent?.email,
    currentAgent?.usuario?.emailreal,
    currentAgent?.usuario?.email,
    currentAgent?.usuario?.grupo,
  ].filter(Boolean)

  return Array.from(new Set(rawEmails.flatMap((email) => getEmailVariants(email))))
}

const decryptPhoneNumber = (encryptedPhoneNumber) => {
  try {
    const bytes = CryptoJS.enc.Base64.parse(encryptedPhoneNumber).toString(CryptoJS.enc.Utf8)
    const originalText = CryptoJS.AES.decrypt(bytes, secretKey).toString(CryptoJS.enc.Utf8)
    return originalText
  } catch (error) {
    console.error("Error decrypting phone number:", error)
    return null // O un valor predeterminado adecuado
  }
}

const getMessageIdentity = (msg) => {
  if (msg?.key) {
    return String(msg.key)
  }

  const time = Number(msg?.time || 0)
  const sender = String(msg?.sender || "")
  const recvId = String(msg?.recvId || "")
  const type = String(msg?.messagetype || "")
  const body = String(msg?.body || "")

  return `fallback-${sender}-${recvId}-${time}-${type}-${body}`
}

const sortByTime = (a, b) => {
  const aTime = Number(a?.time || 0)
  const bTime = Number(b?.time || 0)

  if (aTime === bTime) {
    return getMessageIdentity(a).localeCompare(getMessageIdentity(b))
  }

  return aTime - bTime
}

/**
 * Update the message area to ensure the last message is visible
 * @param {{messageList:Object, loadArchive:(waId:string)=>Array}} props
  */
function CustomMessageArea(props) {
  const [transferDialogIsOpen, setTransferDialogIsOpen] = useState(false)
  const [archiveDialogIsOpen, setArchiveDialogIsOpen] = useState(false)
  const [archivedMessages, setArchivedMessages] = useState([])
  const [showNewMessagesButton, setShowNewMessagesButton] = useState(false)
  const [showGoBottomButton, setShowGoBottomButton] = useState(false)
  const [chatOwnershipLost, setChatOwnershipLost] = useState(false)
  const [imageViewerOpen, setImageViewerOpen] = useState(false)
  const [selectedImageUrl, setSelectedImageUrl] = useState("")
  const [copied, setCopied] = useState(null)
  const [prevLoadingState, setPrevLoadingState] = useState(false)

  const { waid } = useParams()

  const decryptedWaid = decryptPhoneNumber(waid)
  const filter = (msg) => msg.sender === decryptedWaid || msg.recvId === decryptedWaid
  const messages = props.messageList.filter(filter).sort(sortByTime)
  const contact = props.contactList.find((c) => c.id === decryptedWaid) || { name: "" }

  const messageArea = useRef(null)
  const previousConversationRef = useRef("")
  const previousMessageCountRef = useRef(0)
  const wasNearBottomRef = useRef(true)
  const ownershipCleanupDoneRef = useRef(false)
  const ownershipConversationRef = useRef("")

  const agentVariants = getCurrentAgentEmailVariants(props.currentAgent)

  const doesBrAgentMatchCurrentAgent = (assignedBrAgent) => {
    if (!assignedBrAgent) return true

    const assignedTokens = String(assignedBrAgent)
      .split(",")
      .map((token) => token.trim())
      .filter(Boolean)

    if (assignedTokens.length === 0) return true

    return assignedTokens.some((token) => {
      const tokenComparable = normalizeEmailComparable(token)
      return agentVariants.some(
        (variant) => normalizeEmailComparable(variant) === tokenComparable,
      )
    })
  }

  const applyOwnershipLock = () => {
    setChatOwnershipLost(true)

    if (!ownershipCleanupDoneRef.current && props.cleanupMessageListeners) {
      props.cleanupMessageListeners(decryptedWaid)
      ownershipCleanupDoneRef.current = true
    }

    // Remove from current agent list but keep locked chat view open
    if (props.hideConversation) {
      props.hideConversation(decryptedWaid, true)
    }

    if (props.setLoadingConversation) {
      props.setLoadingConversation(false)
    }
  }

  const isConversationAssignedToCurrentAgent = async () => {
    if (agentVariants.length === 0 || !decryptedWaid) return true

    const localContactAgent = contact?.br_agent || ""
    if (localContactAgent) {
      return doesBrAgentMatchCurrentAgent(localContactAgent)
    }

    try {
      const agentSnapshot = await window.firebase
        .database()
        .ref(`contacto/${decryptedWaid}/BrAgent`)
        .once("value")
      const remoteAgent = agentSnapshot.val() || ""
      if (!remoteAgent) return true
      return doesBrAgentMatchCurrentAgent(remoteAgent)
    } catch (error) {
      console.error("No se pudo validar la asignacion del chat", error)
      return true
    }
  }

  // On page refresh, restore selected conversation from route hash.
  useEffect(() => {
    if (!waid || !decryptedWaid || !props.setCurrentConversation) return
    if (!props.currentAgent || !props.currentAgent.usuario) return

    const restoreConversationFromUrl = async () => {
      if (props.currentConversation === decryptedWaid) return

      const isAssigned = await isConversationAssignedToCurrentAgent()
      if (!isAssigned) {
        applyOwnershipLock()
        return
      }

      props.setCurrentConversation(waid, false)
    }

    restoreConversationFromUrl()
  }, [
    waid,
    decryptedWaid,
    props.currentConversation,
    props.setCurrentConversation,
    props.currentAgent,
  ])

  useEffect(() => {
    if (!decryptedWaid) return

    // Reset lock state when changing conversation
    if (ownershipConversationRef.current !== decryptedWaid) {
      setChatOwnershipLost(false)
      ownershipCleanupDoneRef.current = false
      ownershipConversationRef.current = decryptedWaid
    }

    const contactAgent = contact?.br_agent || ""

    // If contact is assigned to another email/group than the current agent variants, lock chat.
    if (contactAgent && !doesBrAgentMatchCurrentAgent(contactAgent)) {
      applyOwnershipLock()
      return
    }
  }, [
    decryptedWaid,
    contact?.br_agent,
    props.currentAgent,
    props.cleanupMessageListeners,
    props.hideConversation,
    props.setLoadingConversation,
  ])

  const isUserNearBottom = () => {
    if (!messageArea.current) return true;
    const { scrollTop, scrollHeight, clientHeight } = messageArea.current;
    return (scrollHeight - scrollTop - clientHeight) <= 40;
  };

  const scrollToBottom = () => {
    if (!messageArea.current) return;
    messageArea.current.scrollTop = messageArea.current.scrollHeight;
    wasNearBottomRef.current = true;
    setShowNewMessagesButton(false)
    setShowGoBottomButton(false)
  };

  const handleScroll = () => {
    wasNearBottomRef.current = isUserNearBottom();
    if (wasNearBottomRef.current) {
      setShowNewMessagesButton(false)
      setShowGoBottomButton(false)
    } else if (!showNewMessagesButton) {
      setShowGoBottomButton(true)
    }
  };

  useEffect(() => {
    // Execute when loading starts (either new conversation or same conversation clicked again)
    if (props.loadingConversation === true && prevLoadingState === false && !chatOwnershipLost) {
      setPrevLoadingState(true);
      setShowNewMessagesButton(false)
      setShowGoBottomButton(false)

      const startConversationLoad = async () => {
        const isAssigned = await isConversationAssignedToCurrentAgent()
        if (!isAssigned) {
          setArchivedMessages([])
          applyOwnershipLock()
          return
        }

        // Load archive and set messages from database
        props
          .loadArchive(decryptedWaid)
          .then((col) => {
            const archivedMessages = col.filter(filter).sort(sortByTime);
            setArchivedMessages(archivedMessages);
            
            // Get the timestamp of the last message from database
            let lastTimestamp = 0;
            if (archivedMessages.length > 0) {
              lastTimestamp = archivedMessages[archivedMessages.length - 1].time;
            }
            
            // Setup Firebase listeners for new messages AFTER database messages are loaded
            // Pass lastTimestamp to only listen for newer messages
            if (props.setupMessageListeners) {
              props.setupMessageListeners(decryptedWaid, lastTimestamp);
            } else {
              console.error("No se pudo inicializar el listener de mensajes")
            }
          })
          .catch((err) => {
            console.error("Error al cargar el historial del chat", err)
          })
          .finally(() => {
            // Stop loading animation
            if (props.setLoadingConversation) {
              props.setLoadingConversation(false);
            }

            scrollToBottom();
          });
      }

      startConversationLoad()

      // Mostrar el área de mensajes y ocultar la lista de chats en móvil
      const chatListArea = document.getElementById("chat-list-area");
      if (chatListArea && window.innerWidth < 768) {
        chatListArea.classList.add("hidden");
      }

      // Ensure the message area is scrollable on mobile
      if (window.innerWidth < 768) {
        document.body.style.overflow = "hidden";
        if (messageArea.current) {
          messageArea.current.style.overflowY = "auto";
        }
      }

      // Add a small delay before scrolling to ensure all content is rendered
      setTimeout(() => {
        scrollToBottom();
      }, 300);
    } else if (props.loadingConversation === false && prevLoadingState === true) {
      setPrevLoadingState(false);
    }
  }, [props.loadingConversation, chatOwnershipLost])

  const uniqueMessageCount = Object.keys(
    archivedMessages.concat(messages).reduce((acc, msg) => {
      acc[getMessageIdentity(msg)] = true;
      return acc;
    }, {})
  ).length

  // Scroll rules:
  // 1) On conversation open/refresh, always scroll to bottom.
  // 2) On new message, scroll only if user was already near bottom.
  // 3) If user is not at bottom, keep current scroll position.
  useEffect(() => {
    const conversationChanged = previousConversationRef.current !== decryptedWaid
    const hasNewMessages = uniqueMessageCount > previousMessageCountRef.current

    if (conversationChanged) {
      previousConversationRef.current = decryptedWaid || ""
      setShowNewMessagesButton(false)
      setShowGoBottomButton(false)
      requestAnimationFrame(scrollToBottom)
    } else if (hasNewMessages) {
      if (wasNearBottomRef.current) {
        requestAnimationFrame(scrollToBottom)
      } else {
        setShowNewMessagesButton(true)
        setShowGoBottomButton(false)
      }
    }

    previousMessageCountRef.current = uniqueMessageCount
  }, [decryptedWaid, uniqueMessageCount])

  const openTransferDialog = () => setTransferDialogIsOpen(true)
  const closeTransferDialog = () => setTransferDialogIsOpen(false)
  const openArchiveDialog = () => setArchiveDialogIsOpen(true)
  const closeArchiveDialog = () => setArchiveDialogIsOpen(false)

  const handleImageClick = (imageUrl) => {
    setSelectedImageUrl(imageUrl)
    setImageViewerOpen(true)
  }

  const closeImageViewer = () => {
    setImageViewerOpen(false)
    setSelectedImageUrl("")
  }

  const handleBackButton = () => {
    const chatListArea = document.getElementById("chat-list-area")
    if (chatListArea) {
      chatListArea.classList.remove("hidden")
    }

    // Reset body overflow when going back to chat list
    document.body.style.overflow = ""
  }

  let transferBtn = ""
  if (props.currentAgent.usuario && props.currentAgent.can(Agente.RULE_CAN_TRANSFER_CONVERSATION)) {
    transferBtn = (
      <Tooltip content="Transferir conversación">
        <Button icon="exchange" minimal="true" large="true" onClick={openTransferDialog} />
      </Tooltip>
    )
  }

  const handleBlur = (e) => {
    const db = window.firebase.database()
    const ref = db.ref(`contacto/${decryptedWaid}`)
    ref.update({ Nombre: e.target.textContent })
  }

  const copyToClipboard = async (text, field)  => {
    await navigator.clipboard.writeText(text);
    setCopied(field);

    setTimeout(() => setCopied(null), 1500);
  };

  return (
    <div className="d-sm-flex flex-column col-12 col-sm-7 col-md-8 p-0 h-100" id="message-area" style={{ position: "relative" }}>
      <div className="w-100 h-100 overlay d-none"></div>

      {/* Loading spinner */}
      {props.loadingConversation && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255, 255, 255, 0.9)",
            zIndex: 100,
            borderRadius: "4px",
          }}
        >
          <Spinner size={40} />
          <p style={{ marginTop: "16px", color: "#666" }}>Cargando conversación...</p>
        </div>
      )}

      {chatOwnershipLost && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255, 255, 255, 0.82)",
            zIndex: 120,
            borderRadius: "4px",
            textAlign: "center",
            padding: "16px",
          }}
        >
          <div
            style={{
              width: "56px",
              height: "56px",
              borderRadius: "50%",
              backgroundColor: "#e7f1ff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <i className="fas fa-info-circle" style={{ color: "#0d6efd", fontSize: "30px" }}></i>
          </div>
          <p style={{ marginTop: "16px", color: "#666", fontWeight: 600 }}>Esta conversacion pertenece a otro agente</p>
          <p style={{ margin: "0", color: "#888" }}>Se detuvo la actualizacion y el envio de mensajes.</p>
        </div>
      )}

      {/*<!-- Navbar -->*/}
      <div className="row d-flex flex-row align-items-center p-2 m-0 w-100" id="navbar">
        <div className="back-button" onClick={handleBackButton}>
          <i className="fas fa-arrow-left"></i>
        </div>
        <Avatar value={decryptedWaid} />
        <div className="d-flex flex-column">
          <div
            className="font-weight-bold"
            id="name"
            onBlur={handleBlur}
            contentEditable
            suppressContentEditableWarning={true}
          >
            {contact.name}
          </div>
          <div className="small" id="details">
            {decryptedWaid}
          </div>
          <div className="campaign-tag">
            {/* <div className="badge badge-dark">
              {contact.ExtraFields?.IdCliente}
            </div>
            <div className="badge badge-dark">
              {contact.ExtraFields?.Producto}
            </div> */}
            <div
              className="badge badge-dark"
              style={{ cursor: "pointer" }}
              onClick={() =>
                copyToClipboard(contact.ExtraFields?.NumeroCliente, "id")
              }
            >
              {copied === "id" ? "Copiado ✅" : contact.ExtraFields?.NumeroCliente}
            </div>
            <div
              className="badge badge-dark"
              style={{ cursor: "pointer" }}
              onClick={() =>
                copyToClipboard(contact.ExtraFields?.Producto, "producto")
              }
            >
              {copied === "producto" ? "Copiado ✅" : contact.ExtraFields?.Producto}
            </div>
          </div>
        </div>
        <div className="d-flex flex-row align-items-center ml-auto">
          {transferBtn}
          <Tooltip content="Liberar conversación">
            <Button icon="archive" minimal="true" large="true" onClick={openArchiveDialog} />
          </Tooltip>
        </div>
      </div>

      {/*<!-- Messages -->*/}
      <div className="d-flex flex-column" id="messages" ref={messageArea} onScroll={handleScroll}>
        {renderMessages(messages, archivedMessages, decryptedWaid, handleImageClick)}
        {/* Add an empty div with padding to ensure the last message is visible */}
        <div style={{ height: window.innerWidth < 768 ? "120px" : "60px" }}></div>
      </div>

      {showNewMessagesButton && (
        <div style={{ position: "absolute", bottom: window.innerWidth < 768 ? "88px" : "72px", left: "50%", transform: "translateX(-50%)", zIndex: 90 }}>
          <button
            type="button"
            onClick={scrollToBottom}
            style={{
              backgroundColor: "#1976d2",
              color: "#fff",
              border: "none",
              borderRadius: "999px",
              padding: "8px 14px",
              fontSize: "13px",
              fontWeight: 600,
              boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
              cursor: "pointer",
            }}
          >
            ↓ mensajes nuevos ↓
          </button>
        </div>
      )}

      {!showNewMessagesButton && showGoBottomButton && (
        <div style={{ position: "absolute", bottom: window.innerWidth < 768 ? "88px" : "72px", left: "50%", transform: "translateX(-50%)", zIndex: 90 }}>
          <button
            type="button"
            onClick={scrollToBottom}
            style={{
              backgroundColor: "#6c757d",
              color: "#fff",
              border: "none",
              borderRadius: "999px",
              padding: "8px 14px",
              fontSize: "13px",
              fontWeight: 600,
              boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
              cursor: "pointer",
            }}
          >
            ↓ ir al final ↓
          </button>
        </div>
      )}

      {/*<!-- Input -->*/}
      <InputArea
        contactList={props.contactList}
        receiver={decryptedWaid}
        currentAgent={props.currentAgent}
        quickReplies={props.quickReplies}
        blockedByOwnership={chatOwnershipLost}
      />

      <TransferDialog
        currentConversation={decryptedWaid}
        setAgentToConversation={props.setAgentToConversation}
        transferDialogIsOpen={transferDialogIsOpen}
        closeTransferDialog={closeTransferDialog}
        agentList={props.agentList}
      />

      <ArchiveDialog
        currentConversation={decryptedWaid}
        archiveDialogIsOpen={archiveDialogIsOpen}
        closeArchiveDialog={closeArchiveDialog}
        hideConversation={props.hideConversation}
        currentAgent={props.currentAgent}
        quickReplies={props.quickReplies}
        typification={props.typification}
      />

      <ImageViewer
        imageUrl={selectedImageUrl}
        isOpen={imageViewerOpen}
        onClose={closeImageViewer}
      />
    </div>
  )
}


function renderMessages(messages, archivedMessages, decryptedWaid, onImageClick) {
  let result = []
  let lastDate;
  let lastAgent;

  const allMessages = archivedMessages.concat(messages)
  const uniqueMessages = Object.values(
  allMessages.reduce((acc, msg) => {
    acc[getMessageIdentity(msg)] = msg;
    return acc;
  }, {})
).sort(sortByTime)

  uniqueMessages.map((msg, i) => {
    const dateMessage = new Date(msg.time).getDate();
    if (lastDate !== dateMessage) {
      result = result.concat(MessageDate({ message: msg, index: i }))
      lastDate = dateMessage
    }

    if (msg.sender === decryptedWaid) {
      result = result.concat(MessageReceived({ message: msg, index: i, onImageClick: onImageClick }));
      return null;
    }

    if (lastAgent !== msg.brAgent && msg.messagetype != "3") {
      result = result.concat(MessageAgent({ message: msg, index: i }))
      lastAgent = msg.brAgent
    }

    if (msg.messagetype == "3" && msg.recvId === decryptedWaid) {
      result = result.concat(MessageSystem({ message: msg, index: i }));
      return null
    }

    result = result.concat(MessageSent({ message: msg, index: i, onImageClick: onImageClick }));
  })
  return result
}

/**
 * Modificar la función MessageArea para ajustar las clases responsivas
 * @param {{messageList:Array, loadArchive:(waId:string)=>Array}} props
 * @returns {JSX.Element}
 * @constructor
 */
function MessageArea(props) {
  const textMessage = props.currentAgent ? "Selecciona una conversación para empezar." : "Inicia sesión para empezar."
  const loaderClass = classNames("col-12 col-sm-7 col-md-8 p-0 h-100 bg-light", {
    "d-flex": !props.appIsLoading,
    "flex-column": !props.appIsLoading,
    "d-none": props.appIsLoading,
  })

  return (
    <Switch>
      <Route path="/conversation/:waid">
        {!props.appIsLoading ? (
          <CustomMessageArea
            loadArchive={props.loadArchive}
            setupMessageListeners={props.setupMessageListeners}
            cleanupMessageListeners={props.cleanupMessageListeners}
            currentAgent={props.currentAgent}
            currentConversation={props.currentConversation}
            setCurrentConversation={props.setCurrentConversation}
            messageList={props.messageList}
            contactList={props.contactList}
            agentList={props.agentList}
            quickReplies={props.quickReplies}
            setAgentToConversation={props.setAgentToConversation}
            hideConversation={props.hideConversation}
            typification={props.typification}
            loadingConversation={props.loadingConversation}
            setLoadingConversation={props.setLoadingConversation}
          />
        ) : null}
      </Route>
      <Route path="/">
        <div className={loaderClass} id="message-area">
          <div className="w-100 h-100 ">
            <div className="container" style={{ minHeight: "100%", alignItems: "center", display: "flex" }}>
              <div className="container">
                <div className="row justify-content-center">
                  <div className="col-4">
                    <img
                      className="blink-me"
                      width="100%"
                      src={SpaceInvader || "/placeholder.svg"}
                      alt="Space Invader"
                    />
                  </div>
                </div>
                <div className="row justify-content-center">
                  <div className="col-12  text-center">{textMessage}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Route>
    </Switch>
  )
}

export default MessageArea
